import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { toast } from "react-toastify";
import styles from "./ReauthModal.module.css";

/**
 * ReauthModal Component
 *
 * First Principle: "Sudo Mode" UI.
 * When a sensitive action is attempted (wallet, voucher, etc.) and the session is
 * considered "stale" (older than 5 mins), this modal challenges the user for their
 * password to prove explicit intent and recent identity verification.
 */
const ReauthModal = ({ isOpen, onClose, onSuccess }) => {
  const { verifySudo, loading } = useAuth();
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!password) {
      setError("Password is required to proceed.");
      return;
    }

    const result = await verifySudo(password);
    if (result.ok && result.sudoToken) {
      setPassword("");
      onSuccess(result.sudoToken);
      onClose();
    } else {
      setError(result.message || "Identity verification failed.");
      setPassword("");
    }
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.header}>
          <div className={styles.iconWrapper}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
            </svg>
          </div>
          <h3>Confirm Identity</h3>
          <p>You're performing a sensitive action. Please re-enter your password to proceed.</p>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.inputGroup}>
            <label htmlFor="sudo-password">Password</label>
            <input
              id="sudo-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your account password"
              autoFocus
              autoComplete="current-password"
            />
            {error && <span className={styles.errorText}>{error}</span>}
          </div>

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.cancelBtn}
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={styles.submitBtn}
              disabled={loading}
            >
              {loading ? "Verifying..." : "Confirm & Continue"}
            </button>
          </div>
        </form>

        <div className={styles.footer}>
          <p>
            For your security, we require recent authentication for sensitive operations like
            voucher redemption or wallet management.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ReauthModal;
